/**
 * EliteKennelEditor — Elite-only editor card on the breeder's own
 * BreederDetail page. Lets the kennel owner manage:
 *   - Pinned testimonials (max 6)
 *   - Award case (max 12)
 *   - Hero video URL + poster URL (uploaded separately via /storage/upload)
 *
 * Auto-hides for breeders not on the Kennel Elite tier (the underlying
 * PUTs return 402 in that case).
 */
import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatApiError } from "@/lib/utils-app";
import { CrownIcon as Crown, TrashIcon as Trash, FilmStripIcon as FilmStrip, PlusIcon as Plus, TrophyIcon as Trophy, QuotesIcon as Quotes } from "@phosphor-icons/react";

const MAX_TESTI = 6;
const MAX_AWARDS = 12;

export default function EliteKennelEditor({ breeder, onSaved }) {
  const [tier, setTier] = useState(null);
  const [tierLoading, setTierLoading] = useState(true);

  // Probe the user's tier so we hide the editor for non-Elite users.
  // (We don't have a shared `useTier` hook — billing/plan-status returns it.)
  useEffect(() => {
    let cancelled = false;
    api.get("/billing/plan-status")
      .then((r) => { if (!cancelled) setTier(r.data?.tier || null); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setTierLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (tierLoading) return null;
  // Only show the editor for Elite kennels (admin can edit any breeder
  // anyway via the same endpoints, but we save the UI clutter).
  if (tier !== "kennel_elite" && tier !== "admin") return null;

  return (
    <div
      className="bg-gradient-to-br from-[#0E0E0E] via-[#1a1a1a] to-[#0E0E0E] text-white rounded-2xl p-6 md:p-7 mb-6 relative overflow-hidden"
      data-testid="elite-kennel-editor"
    >
      <div
        aria-hidden
        className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-gold/15 blur-3xl"
      />
      <div className="relative">
        <div className="flex items-center gap-2 mb-1">
          <Crown size={18} weight="duotone" className="text-gold" />
          <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold">
            Kennel Elite editor
          </span>
        </div>
        <h2 className="font-spectral text-2xl tracking-tight">
          Curate your prestige surfaces
        </h2>
        <p className="text-sm text-white/70 mt-1 max-w-2xl">
          Pinned testimonials, award case, and your hero video appear on{" "}
          <span className="text-gold-light">/k/{breeder.slug || breeder.id}</span> the moment you save.
        </p>

        <HeroVideoEditor breeder={breeder} onSaved={onSaved} />
        <PinnedTestimonialsEditor breeder={breeder} onSaved={onSaved} />
        <AwardCaseEditor breeder={breeder} onSaved={onSaved} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hero video
// ---------------------------------------------------------------------------

function HeroVideoEditor({ breeder, onSaved }) {
  const [videoUrl, setVideoUrl] = useState(breeder.hero_video_url || "");
  const [posterUrl, setPosterUrl] = useState(breeder.hero_video_poster_url || "");
  const [busy, setBusy] = useState(false);
  const videoFileRef = useRef(null);
  const posterFileRef = useRef(null);

  const uploadVideo = async (file, isPoster = false) => {
    if (!file) return;
    if (isPoster && file.size > 10 * 1024 * 1024) {
      toast.error("Poster must be ≤ 10MB.");
      return;
    }
    if (!isPoster && file.size > 50 * 1024 * 1024) {
      toast.error("Hero video must be ≤ 50MB. Keep it short (6–15s) for best results.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = res.data?.url || res.data?.storage_path;
      if (!url) throw new Error("Upload failed");
      if (isPoster) setPosterUrl(url);
      else setVideoUrl(url);
      toast.success(isPoster ? "Poster uploaded." : "Video uploaded — don't forget to save.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/breeders/${breeder.id}/hero-video`, {
        video_url: videoUrl || null,
        poster_url: posterUrl || null,
      });
      toast.success(videoUrl ? "Hero video saved." : "Hero video cleared.");
      onSaved && onSaved({
        hero_video_url: r.data.hero_video_url,
        hero_video_poster_url: r.data.hero_video_poster_url,
      });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mt-6 pt-5 border-t border-white/10">
      <div className="flex items-center gap-2 mb-2">
        <FilmStrip size={16} className="text-gold-light" />
        <h3 className="font-spectral text-lg">Hero video</h3>
      </div>
      <p className="text-xs text-white/60 mb-3">
        6–15 seconds, autoplays muted. ≤ 50MB. Replace the dark-red brand hero on your /k/ page.
        Best results: a slow pan across your kennel, dogs running in a field, or an
        intro shot of the owner with the dogs. Avoid copyrighted background music —
        we may remove the video if a DMCA notice is filed.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider text-white/60 block mb-1">
            Video file
          </label>
          <input
            ref={videoFileRef}
            type="file"
            accept="video/mp4,video/quicktime,video/webm"
            onChange={(e) => uploadVideo(e.target.files?.[0])}
            className="text-xs text-white/80"
            disabled={busy}
            data-testid="elite-hero-video-input"
          />
          {videoUrl && (
            <video
              src={videoUrl}
              muted
              autoPlay
              loop
              playsInline
              className="mt-2 w-full max-w-xs rounded-lg border border-white/10"
            />
          )}
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider text-white/60 block mb-1">
            Poster image (optional)
          </label>
          <input
            ref={posterFileRef}
            type="file"
            accept="image/*"
            onChange={(e) => uploadVideo(e.target.files?.[0], true)}
            className="text-xs text-white/80"
            disabled={busy}
            data-testid="elite-hero-poster-input"
          />
          {posterUrl && (
            <img
              src={posterUrl}
              alt=""
              className="mt-2 w-full max-w-xs rounded-lg border border-white/10"
            />
          )}
        </div>
      </div>

      <div className="mt-3 flex gap-2">
        <Button
          size="sm"
          onClick={save}
          disabled={busy}
          className="bg-gold text-ink hover:bg-gold-light min-h-[44px]"
          data-testid="elite-hero-save"
        >
          Save hero
        </Button>
        {videoUrl && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => { setVideoUrl(""); setPosterUrl(""); }}
            disabled={busy}
            className="border-white/20 text-white/80 hover:bg-white/10 min-h-[44px]"
            data-testid="elite-hero-clear"
          >
            Clear
          </Button>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Pinned testimonials
// ---------------------------------------------------------------------------

function PinnedTestimonialsEditor({ breeder, onSaved }) {
  const [items, setItems] = useState(breeder.pinned_testimonials || []);
  const [busy, setBusy] = useState(false);

  const update = (i, key, value) => {
    const next = items.slice();
    next[i] = { ...(next[i] || {}), [key]: value };
    setItems(next);
  };
  const remove = (i) => setItems(items.filter((_, idx) => idx !== i));
  const add = () => {
    if (items.length >= MAX_TESTI) return;
    setItems([...items, { quote: "", author: "", year: "", source_url: "" }]);
  };

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/breeders/${breeder.id}/pinned-testimonials`, { items });
      setItems(r.data.pinned_testimonials || []);
      toast.success("Testimonials saved.");
      onSaved && onSaved({ pinned_testimonials: r.data.pinned_testimonials });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <section className="mt-6 pt-5 border-t border-white/10">
      <div className="flex items-center gap-2 mb-2">
        <Quotes size={16} className="text-gold-light" />
        <h3 className="font-spectral text-lg">Pinned testimonials</h3>
        <span className="text-xs text-white/50 ml-auto">
          {items.length}/{MAX_TESTI}
        </span>
      </div>
      <p className="text-xs text-white/60 mb-3">
        Specific beats generic. Buyers skim — a quote like
        <em> "Our pup arrived crate-trained and her temperament is exactly what
        they described in our intake call" </em>
        outperforms <em>"great breeder, would buy again"</em> 3-to-1 on conversion.
        Always get the buyer's written permission before pinning.
      </p>

      <div className="space-y-3">
        {items.map((t, i) => (
          <div
            key={i}
            className="bg-white/5 border border-white/10 rounded-lg p-3"
            data-testid={`elite-testi-row-${i}`}
          >
            <Textarea
              value={t.quote || ""}
              onChange={(e) => update(i, "quote", e.target.value)}
              placeholder="Quote (e.g. 'Best breeder we've ever worked with…')"
              rows={2}
              className="bg-white/5 border-white/10 text-white placeholder:text-white/40 mb-2"
            />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <Input
                value={t.author || ""}
                onChange={(e) => update(i, "author", e.target.value)}
                placeholder="Author name"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
              />
              <Input
                value={t.year || ""}
                onChange={(e) => update(i, "year", e.target.value)}
                placeholder="Year (e.g. 2025)"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
              />
              <Input
                value={t.source_url || ""}
                onChange={(e) => update(i, "source_url", e.target.value)}
                placeholder="Source URL (optional)"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
              />
            </div>
            <div className="mt-2 flex justify-end">
              <button
                type="button"
                onClick={() => remove(i)}
                className="text-xs text-white/60 hover:text-crimson flex items-center gap-1 px-3 py-2 min-h-[40px]"
                data-testid={`elite-testi-remove-${i}`}
              >
                <Trash size={14} /> Remove
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-3 flex gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={add}
          disabled={items.length >= MAX_TESTI || busy}
          className="border-white/20 text-white/80 hover:bg-white/10 min-h-[44px]"
          data-testid="elite-testi-add"
        >
          <Plus size={14} className="mr-1" /> Add testimonial
        </Button>
        <Button
          size="sm"
          onClick={save}
          disabled={busy}
          className="bg-gold text-ink hover:bg-gold-light min-h-[44px]"
          data-testid="elite-testi-save"
        >
          Save testimonials
        </Button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Award case
// ---------------------------------------------------------------------------

function AwardCaseEditor({ breeder, onSaved }) {
  const [items, setItems] = useState(breeder.award_case || []);
  const [busy, setBusy] = useState(false);

  const update = (i, key, value) => {
    const next = items.slice();
    next[i] = { ...(next[i] || {}), [key]: value };
    setItems(next);
  };
  const remove = (i) => setItems(items.filter((_, idx) => idx !== i));
  const add = () => {
    if (items.length >= MAX_AWARDS) return;
    setItems([...items, { title: "", year: "", issuer: "", url: "" }]);
  };

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/breeders/${breeder.id}/award-case`, { items });
      setItems(r.data.award_case || []);
      toast.success("Award case saved.");
      onSaved && onSaved({ award_case: r.data.award_case });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <section className="mt-6 pt-5 border-t border-white/10">
      <div className="flex items-center gap-2 mb-2">
        <Trophy size={16} className="text-gold-light" />
        <h3 className="font-spectral text-lg">Award case</h3>
        <span className="text-xs text-white/50 ml-auto">
          {items.length}/{MAX_AWARDS}
        </span>
      </div>
      <p className="text-xs text-white/60 mb-3">
        Conformation titles, working titles (IPO/IGP, BH, OB), health
        clearances published by OFA / PennHIP, breed-club awards, and
        legitimate show wins. Each one with a verification URL gets a{" "}
        <em>Verify ↗</em> link on your public page — buyers love the receipts.
      </p>

      <div className="space-y-2">
        {items.map((a, i) => (
          <div
            key={i}
            className="bg-white/5 border border-white/10 rounded-lg p-3 grid grid-cols-1 sm:grid-cols-12 gap-2"
            data-testid={`elite-award-row-${i}`}
          >
            <Input
              value={a.title || ""}
              onChange={(e) => update(i, "title", e.target.value)}
              placeholder="Award title (e.g. 'Best in Show — Westminster')"
              className="sm:col-span-5 bg-white/5 border-white/10 text-white placeholder:text-white/40"
            />
            <Input
              value={a.year || ""}
              onChange={(e) => update(i, "year", e.target.value)}
              placeholder="Year"
              className="sm:col-span-2 bg-white/5 border-white/10 text-white placeholder:text-white/40"
            />
            <Input
              value={a.issuer || ""}
              onChange={(e) => update(i, "issuer", e.target.value)}
              placeholder="Issuer (optional)"
              className="sm:col-span-2 bg-white/5 border-white/10 text-white placeholder:text-white/40"
            />
            <Input
              value={a.url || ""}
              onChange={(e) => update(i, "url", e.target.value)}
              placeholder="Verification URL"
              className="sm:col-span-2 bg-white/5 border-white/10 text-white placeholder:text-white/40"
            />
            <button
              type="button"
              onClick={() => remove(i)}
              className="sm:col-span-1 text-white/60 hover:text-crimson text-xs flex items-center justify-center min-h-[44px] sm:min-h-0"
              data-testid={`elite-award-remove-${i}`}
              aria-label="Remove award"
            >
              <Trash size={16} />
            </button>
          </div>
        ))}
      </div>

      <div className="mt-3 flex gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={add}
          disabled={items.length >= MAX_AWARDS || busy}
          className="border-white/20 text-white/80 hover:bg-white/10 min-h-[44px]"
          data-testid="elite-award-add"
        >
          <Plus size={14} className="mr-1" /> Add award
        </Button>
        <Button
          size="sm"
          onClick={save}
          disabled={busy}
          className="bg-gold text-ink hover:bg-gold-light min-h-[44px]"
          data-testid="elite-award-save"
        >
          Save awards
        </Button>
      </div>
    </section>
  );
}
